import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { FormGroup, Button, Intent } from '@blueprintjs/core';
import DateTimeInput from 'components/DateTimeInput';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError } from 'components/FormBits';
import moment from 'moment';
import Toast from 'helpers/toaster';
import { currentTime } from 'helpers/time';

// Quick date ranges for a vacation, in the company's timezone. "Rest of this
// week" is today through Friday (through Sunday when today is a weekend day);
// "Next week" is next Monday through Friday.
export const VACATION_RANGES = [
  { value: 'today', label: 'Today', range: (now) => [now.clone().startOf('day'), now.clone().endOf('day')] },
  { value: 'tomorrow', label: 'Tomorrow', range: (now) => [now.clone().add(1, 'day').startOf('day'), now.clone().add(1, 'day').endOf('day')] },
  {
    value: 'week',
    label: 'Rest of this week',
    range: (now) => {
      const day = now.isoWeekday();
      const end = day <= 5 ? now.clone().isoWeekday(5) : now.clone().isoWeekday(7);
      return [now.clone().startOf('day'), end.endOf('day')];
    },
  },
  {
    value: 'next-week',
    label: 'Next week',
    range: (now) => {
      const monday = now.clone().add(1, 'week').isoWeekday(1);
      return [monday.clone().startOf('day'), monday.clone().isoWeekday(5).endOf('day')];
    },
  },
];

const sameMinute = (a, b) => Boolean(a && b) && moment(a).isSame(moment(b), 'minute');

// Which quick range (if any) the dates match, so its chip shows as picked.
const matchingRange = (start, end) => {
  const now = currentTime();
  const match = VACATION_RANGES.find((option) => {
    const [from, to] = option.range(now);
    return sameMinute(start, from) && sameMinute(end, to);
  });
  return match ? match.value : null;
};

// A fresh form still has the store's placeholder dates (both "now", the same
// instant): start it as today, all day. Anything else was chosen by someone.
export const defaultVacationDates = (object) => {
  if (!object) return;
  const untouched = !object.start_date || !object.end_date || moment(object.start_date).isSame(moment(object.end_date), 'minute');
  if (untouched) {
    const [from, to] = VACATION_RANGES[0].range(currentTime());
    object.start_date = from;
    object.end_date = to;
  }
};

export const validateVacationDates = (object) => {
  const errors = {};
  if (!object.start_date) errors.start = 'Choose the first day of the vacation.';
  if (!object.end_date) errors.end = 'Choose when the vacation ends.';
  if (object.start_date && object.end_date && moment(object.end_date).isBefore(moment(object.start_date))) {
    errors.end = 'The vacation ends before it starts. Pick a later end.';
  }
  return errors;
};

// Quick ranges + start and end pickers, shared by both vacation forms.
export const VacationDates = observer(({ object, errors, minDate, onStartChange, onEndChange, idPrefix }) => {
  const pick = (value) => {
    const option = VACATION_RANGES.find((o) => o.value === value);
    if (!option) return;
    const [from, to] = option.range(currentTime());
    onStartChange(from);
    onEndChange(to);
  };
  const start = object.start_date && moment(object.start_date);
  const end = object.end_date && moment(object.end_date);
  const days = start && end && !end.isBefore(start)
    ? end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1
    : null;
  return (
    <React.Fragment>
      <Choice
        label="Quick pick"
        value={matchingRange(object.start_date, object.end_date)}
        onChange={pick}
        options={VACATION_RANGES.map(({ value, label }) => ({ value, label }))}
      />
      <div className="row">
        <div className="col-6" id={`${idPrefix}-start`}>
          <FormGroup label="Starts" labelInfo={<span className="fb-required">*</span>} className={errors.start ? 'has-error' : ''}>
            <DateTimeInput
              placeholder={'Start'}
              minDate={minDate}
              defaultValue={moment(object.start_date)}
              onChange={onStartChange} />
            <FieldError>{errors.start}</FieldError>
          </FormGroup>
        </div>
        <div className="col-6" id={`${idPrefix}-end`}>
          <FormGroup label="Ends" labelInfo={<span className="fb-required">*</span>} className={errors.end ? 'has-error' : ''}>
            <DateTimeInput
              placeholder={'End'}
              minDate={minDate}
              defaultValue={moment(object.end_date)}
              onChange={onEndChange} />
            <FieldError>{errors.end}</FieldError>
          </FormGroup>
        </div>
      </div>
      {days ? (
        <p className="bp3-text-muted" style={{ margin: '-6px 0 0', fontSize: 13 }}>
          {days === 1 ? '1 day' : `${days} days`}, {start.format('ddd, MMM D')}{days > 1 ? ` – ${end.format('ddd, MMM D')}` : ''}
        </p>
      ) : null}
    </React.Fragment>
  );
});

VacationDates.propTypes = {
  object: PropTypes.object.isRequired,
  errors: PropTypes.object,
  minDate: PropTypes.object,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  idPrefix: PropTypes.string,
};

// Scroll to and focus the first field with a problem.
export const focusFirstError = (ids) => {
  const el = ids.map((id) => document.getElementById(id)).find(Boolean);
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const focusable = el.querySelector('input, select') || el.querySelector('button');
  if (focusable) focusable.focus();
};

const STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

// Add or edit a vacation for one person (the page's user, or the row's user).
@inject('userStore') @observer
class VacationModal extends React.Component {

  @observable minDateStart = moment().subtract(5, 'month');
  @observable errors = {};
  @observable showErrors = false;

  componentDidMount() {
    if (this.props.isOpen) this.onOpen();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.onOpen();
  }

  onOpen() {
    this.errors = {};
    this.showErrors = false;
    if (!this.props.vacation) defaultVacationDates(this.props.userStore.vacation.object);
  }

  validate() {
    this.errors = validateVacationDates(this.props.userStore.vacation.object);
    return this.errors;
  }

  persistVacation() {
    const {
      userStore,
      userStore: {
        vacation: {
          object
        }
      },
      user
    } = this.props;
    let promise;

    this.showErrors = true;
    const errors = this.validate();
    if (Object.keys(errors).length) {
      focusFirstError([errors.start && 'vacation-start', errors.end && 'vacation-end'].filter(Boolean));
      return;
    }

    if(this.props.vacation && this.props.vacation.id) {
      promise = userStore.updateUserVacation()
    } else {
      object.user_id = user.id;
      promise = userStore.createUserVacation()
    }

    promise
      .then(this.onSuccessfullCreateOrUpdateVacation.bind(this))
      .catch(this.onFailureCreateOrUpdateVacation.bind(this))
  }

  onSuccessfullCreateOrUpdateVacation(response) {
    this.props.onClose()
  }

  onFailureCreateOrUpdateVacation(error) {
    Toast.show({ message: error.response.data.errors, intent: Intent.DANGER })
  }

  handleCloseVacationDailog() {
    this.props.onClose()
  }

  handleStartDateChange(time) {
    let { object } = this.props.userStore.vacation;
    if(time) {
      object.start_date = moment(time)
    }
    if (this.showErrors) this.validate();
  }

  handleEndDateChange(time) {
    let { object } = this.props.userStore.vacation;
    if(time) {
      object.end_date = moment(time)
    }
    if (this.showErrors) this.validate();
  }

  render() {
    const {
      isOpen,
      user,
      userStore: {
        vacation: {
          isLoading,
          object
        }
      }
    } = this.props;
    const editing = Boolean(this.props.vacation);
    const busy = !!isLoading;
    const close = this.handleCloseVacationDailog.bind(this);
    const errors = this.showErrors ? this.errors : {};
    // The page's user (has .attributes) or the row's user (plain).
    const person = user && (user.attributes || user);
    const userName = person && (person.full_name || person.name || [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email);
    return(
      <FormDrawer
        title={editing ? 'Edit vacation' : 'Add vacation'}
        icon="airplane"
        size="min(760px, 100vw)"
        isOpen={isOpen}
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon={editing ? 'floppy-disk' : 'add'}
              text={editing ? 'Save changes' : 'Add vacation'}
              loading={busy}
              onClick={this.persistVacation.bind(this)}
            />
          </>
        }
      >
        <FormSection
          title={userName ? `Time off for ${userName}` : 'Time off'}
          hint="The days they're away. Pick a quick range or set exact times."
        >
          <VacationDates
            object={object}
            errors={errors}
            idPrefix="vacation"
            onStartChange={this.handleStartDateChange.bind(this)}
            onEndChange={this.handleEndDateChange.bind(this)}
          />
          <FormGroup label="Notes" labelFor="vacation-notes">
            <input
              id="vacation-notes"
              type='text'
              className='bp3-input bp3-fill'
              placeholder='e.g. Family trip, back Monday'
              defaultValue={object.notes}
              onChange={(event) => { object.notes = event.target.value } }
            />
          </FormGroup>
        </FormSection>

        {editing && (
          <FormSection title="Approval" hint="Approve or reject this request.">
            <Choice
              label="Status"
              value={object.status}
              onChange={(value) => { object.status = value; }}
              options={STATUS_OPTIONS}
            />
          </FormSection>
        )}

      </FormDrawer>
    )
  }

};

export default VacationModal;
